/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC boundary; see mem://preferences/lint-debt.md */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Send, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import {
  getLatestTaxNoticeForAdmin,
  upsertTaxNotice,
  setTaxNoticePublished,
  listTaxNoticeSends,
  invokeSendTaxNotice,
  type TaxNoticeRow,
  type TaxNoticeSendRow,
} from "@/lib/taxNotice";

type Env = "sandbox" | "live";

const detectEnv = (): Env => {
  const token = (import.meta as any).env?.VITE_PAYMENTS_CLIENT_TOKEN as string | undefined;
  return token?.startsWith("pk_live_") ? "live" : "sandbox";
};

export const TaxNoticeCard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState<"none" | "dry" | "real">("none");
  const [notice, setNotice] = useState<TaxNoticeRow | null>(null);
  const [sends, setSends] = useState<TaxNoticeSendRow[]>([]);

  const [title, setTitle] = useState("Upcoming billing change");
  const [body, setBody] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [publish, setPublish] = useState(false);

  const env: Env = detectEnv();

  const refresh = async () => {
    setLoading(true);
    try {
      const [n, s] = await Promise.all([getLatestTaxNoticeForAdmin(), listTaxNoticeSends()]);
      setNotice(n);
      setSends(s);
      if (n) {
        setTitle(n.title || "Upcoming billing change");
        setBody(n.body_md || "");
        setEffectiveDate(n.effective_date || "");
        setPublish(!!n.published_at);
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to load tax notice");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleSave = async () => {
    if (!user) return;
    if (publish && !effectiveDate) {
      toast.error("Set an effective date before publishing the banner.");
      return;
    }
    setSaving(true);
    try {
      const row = await upsertTaxNotice(
        {
          title,
          body_md: body,
          effective_date: effectiveDate || null,
          publish,
        },
        user.id,
        notice?.id,
      );
      setNotice(row);
      toast.success(publish ? "Tax notice banner is live" : "Tax notice saved (not published)");
    } catch (err: any) {
      toast.error(err?.message || "Failed to save tax notice");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (next: boolean) => {
    if (!notice) {
      setPublish(next);
      return;
    }
    if (next && !notice.effective_date) {
      toast.error("Set an effective date before publishing.");
      return;
    }
    try {
      const row = await setTaxNoticePublished(notice.id, next);
      setNotice(row);
      setPublish(!!row.published_at);
      toast.success(next ? "Banner published" : "Banner unpublished");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update banner");
    }
  };

  const handleSend = async (dryRun: boolean) => {
    if (!effectiveDate) {
      toast.error("Set an effective date first.");
      return;
    }
    setSending(dryRun ? "dry" : "real");
    try {
      const result = await invokeSendTaxNotice(effectiveDate, dryRun, env);
      if (dryRun) {
        const breakdown = result.labelBreakdown
          ? Object.entries(result.labelBreakdown)
              .map(([k, v]) => `${v} ${k}`)
              .join(", ")
          : "";
        toast.success(
          `Dry run: ${result.recipientCount} recipient(s)${breakdown ? ` (${breakdown})` : ""}`,
        );
      } else {
        toast.success(
          `Queued ${result.enqueued ?? result.recipientCount} email(s)${
            result.failureCount ? ` — ${result.failureCount} failed` : ""
          }`,
        );
      }
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || "Send failed");
    } finally {
      setSending("none");
    }
  };

  const canSend = !!effectiveDate && !!notice?.published_at;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Tax notice
          <Badge variant="outline" className="ml-2 font-normal">
            {env === "live" ? "Live" : "Test"}
          </Badge>
        </CardTitle>
        <CardDescription>
          Dormant by default. The banner only appears once published, and the email only sends when
          you explicitly click below. Use this when re-enabling sales tax / VAT collection.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tax-notice-title">Banner title</Label>
                <Input
                  id="tax-notice-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Upcoming billing change"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tax-notice-date">Effective date</Label>
                <Input
                  id="tax-notice-date"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tax-notice-body">Banner message (optional)</Label>
              <Textarea
                id="tax-notice-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={2}
                placeholder="Leave blank to use the default copy with the effective date."
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-medium">Show banner to logged-in users</div>
                <div className="text-xs text-muted-foreground">
                  When off, the banner is fully hidden and customers see nothing.
                </div>
              </div>
              <Switch
                checked={publish}
                onCheckedChange={(v) => {
                  setPublish(v);
                  if (notice) void handleTogglePublish(v);
                }}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {notice ? "Save changes" : "Create tax notice"}
              </Button>
            </div>

            <div className="rounded-md border border-border p-3 space-y-3">
              <div>
                <div className="text-sm font-semibold">One-time email to active subscribers</div>
                <div className="text-xs text-muted-foreground">
                  Sends to all <strong>active + trialing</strong> subscribers in <strong>{env}</strong>{" "}
                  (excluding comp accounts). Tax label is inferred per recipient from billing
                  country (VAT / sales tax). Idempotency keys prevent duplicate sends.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleSend(true)}
                  disabled={!effectiveDate || sending !== "none"}
                >
                  {sending === "dry" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  Dry run (count only)
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button disabled={!canSend || sending !== "none"}>
                      {sending === "real" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      Send tax notice email
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Send tax notice email?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will queue one email per active subscriber in <strong>{env}</strong>{" "}
                        with effective date <strong>{effectiveDate || "—"}</strong>. Idempotency
                        keys prevent duplicates if you click again, but you should still run a dry
                        run first to confirm the recipient count.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleSend(false)}>
                        Send now
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              {!canSend && (
                <div className="text-xs text-muted-foreground">
                  {!effectiveDate
                    ? "Set an effective date first."
                    : "Publish the banner before sending — keeps banner and email in sync."}
                </div>
              )}
            </div>

            {sends.length > 0 && (
              <div className="rounded-md border border-border">
                <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Send history
                </div>
                <ul className="divide-y divide-border text-sm">
                  {sends.map((s) => (
                    <li key={s.id} className="flex items-center justify-between px-3 py-2">
                      <div>
                        <div>
                          {s.dry_run ? "Dry run" : "Sent"} · {s.recipient_count} recipient
                          {s.recipient_count === 1 ? "" : "s"} · effective{" "}
                          <strong>{s.effective_date}</strong>
                        </div>
                        {s.notes && (
                          <div className="text-xs text-muted-foreground">{s.notes}</div>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(s.sent_at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

